import { useEffect, useRef } from 'react';
import {
  Colors,
  Flex,
  List,
  type ListRef,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { isNotNil } from 'shared/src/utils';
import { TabSlotText } from './TabSlotText';
import { TabOverlayImage } from './TabOverlayImage';
import type { HomeTabRoute } from '../HomeContents';
import type {
  SceneRendererProps,
  NavigationState,
} from 'react-native-tab-view';
import { useLogDisplayUpperTab } from '../../../hooks/useLogDisplayUpperTab';

type HomeTabBarProps = SceneRendererProps & {
  navigationState: NavigationState<HomeTabRoute>;
  onTabClick?: (index: number) => void;
};

const TAB_BAR_HEIGHT = 44;
// 탭의 50% 이상이 가로 뷰포트에 들어오면 노출로 판정.
const VIEWABILITY_CONFIG = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 300,
};

/**
 * 상단 카테고리 탭바.
 * react-native-tab-view 의 TabBar 대신 가로 List 로 직접 렌더하여
 * onViewableItemsChanged 로 탭 노출(DISPLAY_UPPER_TAB)을 추적한다.
 * (TabBar 는 내부 스크롤/viewability 를 노출하지 않아 노출 로깅이 불가)
 */
export const HomeTabBar = ({
  onTabClick,
  jumpTo,
  navigationState,
}: HomeTabBarProps) => {
  const { width: screenWidth } = useScreenSize();
  const listRef = useRef<ListRef<HomeTabRoute> | null>(null);
  const { onViewableItemsChanged } = useLogDisplayUpperTab();

  const activeIndex = navigationState.index;
  const routes = navigationState.routes;

  // 활성 탭이 항상 보이도록 auto-scroll (TabBar 의 auto-center 대체).
  useEffect(() => {
    listRef.current?.scrollToIndex({
      index: activeIndex,
      viewPosition: 0.5,
      animated: true,
    });
  }, [activeIndex]);

  return (
    <Flex alignItems={'center'}>
      <Flex
        width={screenWidth}
        height={TAB_BAR_HEIGHT}
        backgroundColorValue={Colors.bg_always_white}
        borderBottomWidth={1}
        borderColor={'border_tertiary'}
      >
        <List<HomeTabRoute>
          ref={listRef}
          data={routes}
          horizontal
          showsHorizontalScrollIndicator={false}
          keyExtractor={item => item.key}
          contentContainerStyle={{
            paddingHorizontal: 16,
            alignItems: 'center',
          }}
          ItemSeparatorComponent={() => <Flex width={16} />}
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={VIEWABILITY_CONFIG}
          // 레이아웃 전 scrollToIndex 실패 시 crash 방지.
          onScrollToIndexFailed={() => {}}
          renderItem={({ item, index }) => {
            const isFocused = index === activeIndex;
            const render = item.render;

            const onPress = () => {
              // onTabClick 이 isTabClickRef 를 세팅한 뒤 jumpTo 가 index 를 바꿔야
              // handleIndexChange 가 SWIPE 로 중복 로깅하지 않는다. 순서 중요.
              onTabClick?.(index);
              jumpTo(item.key);
            };

            const overlayImage = render?.overlay_image;
            const source = isFocused
              ? overlayImage?.selected_source
              : overlayImage?.unselected_source;
            const imageUrl = source?.url;

            const overlayTextUnselected =
              render?.overlay_text?.unselected ?? null;

            return (
              <Flex
                onPress={onPress}
                height={TAB_BAR_HEIGHT}
                padding={{ horizontal: 2 }}
                justifyContent={'center'}
                position={'relative'}
              >
                {isNotNil(overlayImage) && isNotNil(imageUrl) ? (
                  <TabOverlayImage
                    uri={imageUrl}
                    width={overlayImage.width ?? 48}
                    height={overlayImage.height ?? 44}
                    animation={source?.animation ?? null}
                  />
                ) : (
                  <Flex position={'relative'}>
                    {isNotNil(overlayTextUnselected) && !isFocused && (
                      <Flex
                        position={'absolute'}
                        top={2}
                        left={0}
                        right={0}
                        alignItems={'center'}
                        zIndex={1}
                      >
                        <TabSlotText overlayText={overlayTextUnselected} />
                      </Flex>
                    )}
                    <Typography
                      numberOfLines={1}
                      type={isFocused ? 'subtitle1.5' : 'body1.5'}
                      rawColor={
                        isFocused
                          ? (render?.selected_text_color ??
                            Colors.content_primary)
                          : (render?.text_color ?? Colors.content_secondary)
                      }
                    >
                      {item.name}
                    </Typography>
                  </Flex>
                )}

                {render?.is_ad && (
                  <Flex position={'absolute'} top={4} right={-8}>
                    <Typography type={'subtitle7'} rawColor={'#999'}>
                      AD
                    </Typography>
                  </Flex>
                )}

                {/* active 인디케이터 (하단 2px). TabBar 애니메이션 인디케이터 대신
                    활성 탭 고정 underline. */}
                {isFocused && (
                  <Flex
                    position={'absolute'}
                    bottom={0}
                    left={0}
                    right={0}
                    height={2}
                    backgroundColorValue={
                      render?.selected_text_color ?? Colors.content_primary
                    }
                  />
                )}
              </Flex>
            );
          }}
        />
      </Flex>
    </Flex>
  );
};
